import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { MetroStation } from '../domain/types';
import { LineDots } from './LineDots';

interface StationAutocompleteProps {
  stations: readonly MetroStation[];
  value: string | null;
  onChange: (stationId: string | null) => void;
  error?: string;
  disabled?: boolean;
}

function normalize(value: string): string {
  return value.toLocaleLowerCase('ru').replaceAll('ё', 'е').trim();
}

export function StationAutocomplete({ stations, value, onChange, error, disabled }: StationAutocompleteProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = stations.find((station) => station.id === value) ?? null;
  const [text, setText] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setText(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  const suggestions = useMemo(() => {
    const query = normalize(text);
    const matching = query
      ? stations.filter((station) => normalize(station.name).includes(query))
      : stations;
    return matching.slice(0, 10);
  }, [stations, text]);

  useEffect(() => {
    setActiveIndex(0);
  }, [text]);

  const choose = (station: MetroStation) => {
    setText(station.name);
    onChange(station.id);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="autocomplete">
      <input
        ref={inputRef}
        className="field-input"
        type="text"
        value={text}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && suggestions.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && suggestions[activeIndex] ? `${listId}-${activeIndex}` : undefined}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${listId}-error` : undefined}
        placeholder="Начните вводить название"
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(event) => {
          const nextText = event.target.value;
          setText(nextText);
          if (!selected || normalize(nextText) !== normalize(selected.name)) onChange(null);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && open && suggestions[activeIndex]) {
            event.preventDefault();
            choose(suggestions[activeIndex]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && suggestions.length > 0 ? (
        <ul className="autocomplete-list" id={listId} role="listbox">
          {suggestions.map((station, index) => (
            <li key={station.id} role="presentation">
              <button
                id={`${listId}-${index}`}
                className={`autocomplete-option ${activeIndex === index ? 'autocomplete-option--active' : ''}`}
                type="button"
                role="option"
                aria-selected={activeIndex === index}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(station)}
              >
                <span>{station.name}</span>
                <LineDots lines={station.lines} labelled={false} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open && text.trim() && suggestions.length === 0 ? (
        <p className="field-hint">Станция не найдена. Выберите значение из справочника.</p>
      ) : null}
      {error ? <p className="field-error" id={`${listId}-error`}>{error}</p> : null}
    </div>
  );
}
